import { Form, Formik } from "formik";
import { observer } from "mobx-react";
import moment from "moment";
import * as React from "react";
import * as Yup from "yup";
import { t } from "../../i18n/util";
import { applicationStore } from "../../stores/ApplicationStore";
import { ApplicationValues } from "../../types/models";
import { useMobileMediaQuery } from "../hooks/useMobileMediaQuery";
import { ErrorMessagesList, FormButtons } from "../ui/Primitives";
import { YupLocalizedNumber } from "../util/helpers";
import { DetailsGrid } from "./DetailsGrid";
import { THEME } from "../../theme/ThemeStore";

type Props = {
    onSubmit: (application: ApplicationValues) => void;
    onBack: () => void;
};

export const DetailsForm = observer(({ onSubmit, onBack }: Props) => {
    const isMobile = useMobileMediaQuery();
    const isDal = THEME.THEME_NAME === "dal";

    return (
        <>
            <div style={{ marginBottom: isMobile ? 23 : 36 }}>
                <h3 style={{ marginBottom: isDal ? 12 : undefined }}>{t("screen.details.title")}</h3>
                {isDal && <p className="applicationCaption">{t("screen.details.caption")}</p>}
            </div>
            <Formik
                initialValues={applicationStore.currentApplication}
                validationSchema={Yup.object().shape({
                    salary: YupLocalizedNumber().min(1, t("error.salary")).required(t("error.required")),
                    experience: Yup.number().required(t("error.required")),
                    startDate: Yup.date()
                        .nullable()
                        .min(moment().toDate(), t("error.startDate"))
                        .typeError(t("error.date"))
                        .required(t("error.required")),
                    noticePeriod: YupLocalizedNumber().min(0, t("error.noticePeriod")).nullable(),
                    workHours: YupLocalizedNumber().min(0, t("error.workHours")).required(t("error.required")),
                    germanLvl: isDal
                        ? Yup.string().nullable()
                        : Yup.string().nullable().required(t("error.required")),
                    device: Yup.string().required(t("error.required"))
                })}
                onSubmit={onSubmit}
                validateOnChange
                validateOnBlur
                validateOnMount
            >
                {({ errors, isValid, touched, setFieldValue, dirty, values, setTouched }) => {
                    React.useEffect(() => {
                        applicationStore.currentApplication = values;
                        if (!isValid || (!applicationStore.isStepFinished["details"] && !dirty)) {
                            applicationStore.canNavigate = false;
                            applicationStore.validateForm = () =>
                                setTouched(
                                    {
                                        salary: true,
                                        experience: true,
                                        startDate: true,
                                        noticePeriod: true,
                                        workHours: true,
                                        germanLvl: isDal ? false : true,
                                        device: true
                                    },
                                    true
                                );
                        } else {
                            applicationStore.canNavigate = true;
                        }

                        if (values && dirty && isValid) {
                            applicationStore.isStepFinished["details"] = true;
                        }
                    }, [dirty, errors, isValid, setTouched, values]);

                    return (
                        <Form style={{ height: "100%", flexGrow: 1 }}>
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    flexWrap: "wrap",
                                    height: "100%",
                                    justifyContent: isMobile ? "flex-start" : "space-between"
                                }}
                            >
                                <div>
                                    <DetailsGrid errors={errors} touched={touched} setFieldValue={setFieldValue} />
                                    <ErrorMessagesList errors={errors} touched={touched} />
                                </div>

                                <FormButtons
                                    labelBack={t("screen.details.button.back")}
                                    labelSubmit={t("screen.details.button.toDataUpload")}
                                    middleButtons
                                    onBack={onBack}
                                    style={{ marginTop: 18 }}
                                />
                            </div>
                        </Form>
                    );
                }}
            </Formik>
        </>
    );
});
